import React, { useState, useEffect } from "react";
import { 
  Card, 
  TextField, 
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Pagination
} from "@mui/material";
import { Search as SearchIcon, Star, StarBorder } from "@mui/icons-material";
import { AiTwotoneLike, AiTwotoneDislike } from "react-icons/ai";
import Avatar from "@mui/material/Avatar";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Data
import axios from "axios";

function Ratings() {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  // Filter states
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [journeyIdFilter, setJourneyIdFilter] = useState('');
  const [ratingValueMin, setRatingValueMin] = useState('');
  const [ratingValueMax, setRatingValueMax] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const fetchFilterData = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Fetch drivers
      const driversResponse = await axios.get("https://api.csc-taxi.tech/api/admin/driver", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });
      if (driversResponse.data.status === 1) {
        setDrivers(driversResponse.data.data);
      }

      // Fetch clients
      const clientsResponse = await axios.get("https://api.csc-taxi.tech/api/admin/client", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        }
      });
      if (clientsResponse.data.status === 1) {
        setClients(clientsResponse.data.data);
      }
    } catch (err) {
      console.error('Error fetching filter data:', err);
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // Build filter parameters
      const params = {
        page: currentPage
      };

      // Handle driver/client filters
      if (selectedDriver) {
        params.user_id = selectedDriver;
        params.role = 'driver';
      } else if (selectedClient) {
        params.user_id = selectedClient;
        params.role = 'client';
      }

      // Handle journey ID filter
      if (journeyIdFilter && journeyIdFilter.trim() !== '') {
        params.journey_id = parseInt(journeyIdFilter);
      }

      // Handle rating filters
      if (ratingValueMin && ratingValueMin.trim() !== '') {
        params.rating_value_min = parseInt(ratingValueMin);
      }
      if (ratingValueMax && ratingValueMax.trim() !== '') {
        params.rating_value_max = parseInt(ratingValueMax);
      }

      // Handle search filter
      if (searchFilter && searchFilter.trim() !== '') {
        params.search = searchFilter;
      }

      console.log('Fetching ratings with params:', params);

      const response = await axios.get("https://api.csc-taxi.tech/api/admin/rating", {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Accept-Language': 'ar'
        },
        params: params
      });

      console.log('API Response:', response.data);

      if (response.data.status === 1) {
        setRatings(response.data.data.data);
        setTotalPages(response.data.data.last_page);
        setTotalItems(response.data.data.total);
        setItemsPerPage(response.data.data.per_page);
        setFrom(response.data.data.from);
        setTo(response.data.data.to);
      } else {
        setError("Failed to fetch ratings");
      }
    } catch (err) {
      console.error('Error fetching ratings:', err);
      if (err.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/authentication/sign-in";
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Update filter handlers
  const handleJourneyIdChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setJourneyIdFilter(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleRatingMinChange = (e) => {
    const value = e.target.value;
    if (value === '' || (value >= 1 && value <= 5)) {
      setRatingValueMin(value);
      setCurrentPage(1); // Reset to first page when filter changes
    }
  };

  const handleRatingMaxChange = (e) => {
    const value = e.target.value;
    if (value === '' || (value >= 1 && value <= 5)) {
      setRatingValueMax(value);
      setCurrentPage(1); // Reset to first page when filter changes
    }
  };

  const handleClearFilters = () => {
    setSelectedDriver('');
    setSelectedClient('');
    setJourneyIdFilter('');
    setRatingValueMin('');
    setRatingValueMax('');
    setSearchFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const formControlSx = {
    marginTop: 0,
    marginBottom: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      borderRadius: '8px',
      color: '#333',
      '& .MuiSelect-select': {
        color: '#333 !important',
        WebkitTextFillColor: '#333 !important',
      },
      '& fieldset': {
        borderColor: '#BDBDBD',
      },
      '&:hover fieldset': {
        borderColor: '#333',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#333',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff',
      fontWeight: 400,
      fontSize: 14,
      top: '-3px',
      background: 'transparent !important',
      px: 0.5,
      '&.Mui-focused': {
        color: '#fff',
        background: 'transparent !important',
        px: 0,
      },
      '&.MuiInputLabel-shrink': {
        top: '-6px',
        background: 'transparent !important',
      }
    },
    '& .MuiSvgIcon-root': {
      color: '#333',
    },
  };

  const textFieldSx = {
    marginTop: 0,
    marginBottom: 0,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
      borderRadius: '8px',
      color: '#333',
      '& fieldset': {
        borderColor: '#BDBDBD',
      },
      '&:hover fieldset': {
        borderColor: '#333',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#333',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff',
      fontWeight: 400,
      fontSize: 14,
      top: '-3px',
      background: 'transparent !important',
      px: 0.5,
      '&.Mui-focused': {
        color: '#fff',
        background: 'transparent !important',
        px: 0,
      },
      '&.MuiInputLabel-shrink': {
        top: '-6px',
        background: 'transparent !important',
      }
    },
    '& .MuiSvgIcon-root': {
      color: '#333',
    },
  };

  const columns = [
    { name: "User", align: "left" },
    { name: "Journey ID", align: "center" },
    { name: "Role", align: "center" },
    { name: "Rating", align: "center" },
    { name: "Review", align: "left" },
    { name: "Type", align: "center" },
    { name: "Date", align: "center" },
  ];

  const rows = ratings.map((rating) => ({
    User: (
      <VuiBox display="flex" alignItems="center">
        <Avatar src={rating.user.profile_photo} alt={`${rating.user.first_name} ${rating.user.last_name}`} sx={{ mr: 1 }} />
        <VuiTypography variant="button" color="white" fontWeight="medium">
          {rating.user.first_name} {rating.user.last_name}
        </VuiTypography>
      </VuiBox>
    ),
    "Journey ID": (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {rating.ratingable_id}
      </VuiTypography>
    ),
    Role: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {rating.role}
      </VuiTypography>
    ),
    Rating: (
      <VuiBox display="flex" alignItems="center" justifyContent="center">
        {[...Array(5)].map((_, index) => {
          return index < rating.rating_value ? (
            <Star key={index} sx={{ color: "gold", fontSize: 18 }} />
          ) : (
            <StarBorder key={index} sx={{ color: "gold", fontSize: 18 }} />
          );
        })}
      </VuiBox>
    ),
    Review: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {rating.review}
      </VuiTypography>
    ),
    Type: (
      <VuiBox display="flex" alignItems="center" justifyContent="center">
        {rating.is_complaint ? (
          <AiTwotoneDislike size={24} color="#ff4d4f" />
        ) : (
          <AiTwotoneLike size={24} color="#52c41a" />
        )}
      </VuiBox>
    ),
    Date: (
      <VuiTypography variant="button" color="white" fontWeight="medium">
        {new Date(rating.created_at).toLocaleDateString()}
      </VuiTypography>
    ),
  }));

  // Update useEffect to remove debounce temporarily for testing
  useEffect(() => {
    console.log('Filter values changed:', {
      journeyIdFilter,
      ratingValueMin,
      ratingValueMax,
      selectedDriver,
      selectedClient,
      searchFilter,
      currentPage
    });
    fetchRatings();
  }, [selectedDriver, selectedClient, journeyIdFilter, ratingValueMin, ratingValueMax, searchFilter, currentPage]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Card>
            {/* Top section with search bar and Filters button */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb="22px" p={2}>
              <VuiBox display="flex" alignItems="center" gap={2}>
                <TextField
                  placeholder="Search ratings..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  sx={{
                    ...textFieldSx,
                    minWidth: 250,
                    '& .MuiOutlinedInput-input': {
                      color: '#333 !important',
                      WebkitTextFillColor: '#333 !important',
                    },
                  }}
                />
                <VuiButton
                  variant="contained"
                  color="info"
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ 
                    backgroundColor: '#1E2A49 !important', 
                    color: 'white !important', 
                    minWidth: 100
                  }}
                >
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </VuiButton>
              </VuiBox>
            </VuiBox>

            {/* Collapsible Filters section */}
            {showFilters && (
              <VuiBox mb={2} p={2} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{
                      minWidth: 220,
                      width: 220,
                      mb: 0,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        color: '#333',
                        border: '1px solid #BDBDBD',
                        '& fieldset': { border: '1px solid #BDBDBD' },
                        '&:hover fieldset': { border: '1px solid #333' },
                        '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                        '& .MuiSelect-select': {
                          color: '#333 !important',
                          WebkitTextFillColor: '#333 !important',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#333',
                        fontWeight: 400,
                        fontSize: 14,
                        top: '-3px',
                        background: 'transparent !important',
                        px: 0.5,
                        '&.Mui-focused': {
                          color: '#fff',
                          background: 'transparent !important',
                          px: 0,
                          top: '-3px',
                        },
                        '&.MuiInputLabel-shrink': {
                          color: '#fff',
                          background: 'transparent !important',
                          top: '-6px',
                        },
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#333',
                      },
                    }}>
                      <InputLabel>Driver</InputLabel>
                      <Select
                        value={selectedDriver}
                        label="Driver"
                        onChange={(e) => setSelectedDriver(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#fff',
                              color: '#222',
                              boxShadow: 3,
                              mt: 1,
                              borderRadius: '0px',
                            },
                          },
                          MenuListProps: {
                            sx: {
                              padding: 0,
                            },
                          },
                        }}
                      >
                        <MenuItem value=""
                          sx={{
                            color: '#222',
                            fontWeight: 500,
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '0px',
                            '&.Mui-selected': {
                              backgroundColor: '#e0e0e0 !important',
                              borderRadius: '0px',
                            },
                            '&:hover': {
                              backgroundColor: '#f0f0f0 !important',
                              borderRadius: '0px',
                            },
                          }}
                        >
                          All Drivers
                        </MenuItem>
                        {drivers.map(driver => (
                          <MenuItem 
                            key={driver.id} 
                            value={driver.id}
                            sx={{
                              borderRadius: '0px',
                              color: '#222',
                              backgroundColor: '#fff',
                              '&.Mui-selected': {
                                backgroundColor: '#e0e0e0 !important',
                                borderRadius: '0px',
                              },
                              '&:hover': {
                                backgroundColor: '#f0f0f0 !important',
                                borderRadius: '0px',
                              },
                            }}
                          >
                            {driver.first_name} {driver.last_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{
                      minWidth: 220,
                      width: 220,
                      mb: 0,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        color: '#333',
                        border: '1px solid #BDBDBD',
                        '& fieldset': { border: '1px solid #BDBDBD' },
                        '&:hover fieldset': { border: '1px solid #333' },
                        '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                        '& .MuiSelect-select': {
                          color: '#333 !important',
                          WebkitTextFillColor: '#333 !important',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#333',
                        fontWeight: 400,
                        fontSize: 14,
                        top: '-3px',
                        background: 'transparent !important',
                        px: 0.5,
                        '&.Mui-focused': {
                          color: '#fff',
                          background: 'transparent !important',
                          px: 0,
                          top: '-3px',
                        },
                        '&.MuiInputLabel-shrink': {
                          color: '#fff',
                          background: 'transparent !important',
                          top: '-6px',
                        },
                      },
                      '& .MuiSvgIcon-root': {
                        color: '#333',
                      },
                    }}>
                      <InputLabel>Client</InputLabel>
                      <Select
                        value={selectedClient}
                        label="Client"
                        onChange={(e) => setSelectedClient(e.target.value)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#fff',
                              color: '#222',
                              boxShadow: 3,
                              mt: 1,
                              borderRadius: '0px',
                            },
                          },
                          MenuListProps: {
                            sx: {
                              padding: 0,
                            },
                          },
                        }}
                      >
                        <MenuItem value=""
                          sx={{
                            color: '#222',
                            fontWeight: 500,
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '0px',
                            '&.Mui-selected': {
                              backgroundColor: '#e0e0e0 !important',
                              borderRadius: '0px',
                            },
                            '&:hover': {
                              backgroundColor: '#f0f0f0 !important',
                              borderRadius: '0px',
                            },
                          }}
                        >
                          All Clients
                        </MenuItem>
                        {clients.map(client => (
                          <MenuItem 
                            key={client.id} 
                            value={client.id}
                            sx={{
                              borderRadius: '0px',
                              color: '#222',
                              backgroundColor: '#fff',
                              '&.Mui-selected': {
                                backgroundColor: '#e0e0e0 !important',
                                borderRadius: '0px',
                              },
                              '&:hover': {
                                backgroundColor: '#f0f0f0 !important',
                                borderRadius: '0px',
                              },
                            }}
                          >
                            {client.first_name} {client.last_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Journey ID"
                      value={journeyIdFilter}
                      onChange={handleJourneyIdChange}
                      size="small"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      sx={{
                        minWidth: 220,
                        width: 220,
                        mb: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          color: '#333',
                          border: '1px solid #BDBDBD',
                          '& fieldset': { border: '1px solid #BDBDBD' },
                          '&:hover fieldset': { border: '1px solid #333' },
                          '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          '& .MuiInputBase-input': {
                            color: '#333 !important',
                            WebkitTextFillColor: '#333 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#333',
                          fontWeight: 400,
                          fontSize: 14,
                          top: '-3px',
                          background: 'transparent !important',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#fff',
                            background: 'transparent !important',
                            px: 0,
                            top: '-3px',
                          },
                          '&.MuiInputLabel-shrink': {
                            color: '#fff',
                            background: 'transparent !important',
                            top: '-6px',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#333',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Min Rating"
                      type="number"
                      value={ratingValueMin}
                      onChange={handleRatingMinChange}
                      size="small"
                      inputProps={{ min: 1, max: 5 }}
                      sx={{
                        minWidth: 220,
                        width: 220,
                        mb: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          color: '#333',
                          border: '1px solid #BDBDBD',
                          '& fieldset': { border: '1px solid #BDBDBD' },
                          '&:hover fieldset': { border: '1px solid #333' },
                          '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          '& .MuiInputBase-input': {
                            color: '#333 !important',
                            WebkitTextFillColor: '#333 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#333',
                          fontWeight: 400,
                          fontSize: 14,
                          top: '-3px',
                          background: 'transparent !important',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#fff',
                            background: 'transparent !important',
                            px: 0,
                            top: '-3px',
                          },
                          '&.MuiInputLabel-shrink': {
                            color: '#fff',
                            background: 'transparent !important',
                            top: '-6px',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#333',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Max Rating"
                      type="number"
                      value={ratingValueMax}
                      onChange={handleRatingMaxChange}
                      size="small"
                      inputProps={{ min: 1, max: 5 }}
                      sx={{
                        minWidth: 220,
                        width: 220,
                        mb: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          color: '#333',
                          border: '1px solid #BDBDBD',
                          '& fieldset': { border: '1px solid #BDBDBD' },
                          '&:hover fieldset': { border: '1px solid #333' },
                          '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                          '& .MuiInputBase-input': {
                            color: '#333 !important',
                            WebkitTextFillColor: '#333 !important',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#333',
                          fontWeight: 400,
                          fontSize: 14,
                          top: '-3px',
                          background: 'transparent !important',
                          px: 0.5,
                          '&.Mui-focused': {
                            color: '#fff',
                            background: 'transparent !important',
                            px: 0,
                            top: '-3px',
                          },
                          '&.MuiInputLabel-shrink': {
                            color: '#fff',
                            background: 'transparent !important',
                            top: '-6px',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#333',
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <VuiBox display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="text"
                    onClick={handleClearFilters}
                    sx={{ color: 'white !important' }}
                  >
                    Clear Filters
                  </Button>
                </VuiBox>
              </VuiBox>
            )}

            {/* Table */}
            <VuiBox
              sx={{
                backgroundColor: "rgba(255,255,255,0.05)",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <Table columns={columns} rows={rows} />
              
              {/* Pagination */}
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                <VuiTypography variant="button" color="white" fontWeight="medium">
                  Showing {from} to {to} of {totalItems} entries
                </VuiTypography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#fff',
                    },
                    '& .Mui-selected': {
                      background: '#2196f3 !important',
                    },
                  }}
                />
              </VuiBox>
            </VuiBox>
          </Card>
        </VuiBox>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Ratings; 